import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError, errorText } from '../api.ts'
import { Button } from '../components/Button.tsx'
import { LogoMark } from '../components/Logo.tsx'
import { Field, Loading, Notice } from '../ui.tsx'

// Where a set-password link opens (server/src/repos/localSignin.ts). The link's token is in
// the address's fragment, which never reaches a server, and is sent to ICE's sign-in, not to
// the dashboard's API: nobody is signed in yet.

interface Link {
  email: string
  companyName: string
  mode: 'new' | 'reset' | 'claim' | 'join' | 'blocked'
}

const MIN_LENGTH = 12

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`/signin/password/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-ICE-Dashboard': '1' },
    body: JSON.stringify(body),
    credentials: 'same-origin',
  })
  const data = res.headers.get('Content-Type')?.includes('application/json') ? await res.json() : null
  if (!res.ok) {
    const error = (data as { error?: { code?: string; message?: string } } | null)?.error
    throw new ApiError(res.status, error?.code ?? 'ERROR', error?.message ?? `Something went wrong (${res.status})`)
  }
  return data as T
}

export function SetPassword() {
  // Read once, then taken out of the address bar and the browser's history.
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token'))
  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname)
  }, [])
  const link = useQuery({
    queryKey: ['set-password', token],
    queryFn: () => post<Link>('link', { token }),
    enabled: Boolean(token),
    staleTime: Infinity,
  })

  let body
  if (!token) body = <Notice>This link isn't complete. Open the whole link you were sent, or ask your administrator for a new one.</Notice>
  else if (link.isPending) body = <Loading />
  else if (link.isError) body = <Notice>{errorText(link.error)}</Notice>
  else if (link.data.mode === 'blocked') {
    body = <Notice>The password for {link.data.email} is being reset. Try this link again once it has been set.</Notice>
  } else body = <PasswordForm token={token} link={link.data} />

  return (
    <div className="centered">
      <div className="card narrow">
        <LogoMark size={56} />
        {body}
      </div>
    </div>
  )
}

function PasswordForm({ token, link }: { token: string; link: Link }) {
  const joining = link.mode === 'join'
  const [password, setPassword] = useState('')
  const [again, setAgain] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: () => post('set', { token, password }),
    // Signed in: the dashboard opens on their company.
    onSuccess: () => window.location.assign('/'),
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setProblem(null)
    if (!joining && password.length < MIN_LENGTH) return setProblem(`Use at least ${MIN_LENGTH} characters.`)
    if (!joining && password !== again) return setProblem("The two passwords don't match.")
    save.mutate()
  }

  return (
    <form className="form" onSubmit={submit}>
      <h1>{joining ? `Add ${link.companyName}` : link.mode === 'reset' ? 'Choose a new password' : 'Choose a password'}</h1>
      <p className="muted">
        {joining
          ? `You already sign in to Icefield as ${link.email}. Enter your password to use it at ${link.companyName} too.`
          : `You'll sign in to ${link.companyName}'s Icefield apps as ${link.email}.`}
      </p>
      <Notice>{problem ?? (save.isError ? errorText(save.error) : null)}</Notice>
      {/* For password managers: the account this password is for. */}
      <input type="email" name="username" autoComplete="username" value={link.email} readOnly hidden />
      <Field label="Password" hint={joining ? undefined : `At least ${MIN_LENGTH} characters. A few words together is easy to remember.`}>
        <input
          type="password"
          required
          autoFocus
          maxLength={256}
          autoComplete={joining ? 'current-password' : 'new-password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </Field>
      {!joining && (
        <Field label="Password again">
          <input type="password" required maxLength={256} autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} />
        </Field>
      )}
      <div className="form-actions">
        <Button type="submit" size="large" fullWidth disabled={save.isPending || save.isSuccess}>
          {joining ? `Add ${link.companyName}` : 'Set password and sign in'}
        </Button>
      </div>
    </form>
  )
}
